// AttendancePage - Trainer/Admin dashboard + Candidate history
import { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { WorkspaceLayout } from "@/components/layout/WorkspaceLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  UserCheck, Loader2, Download, Filter, Calendar, Users,
  CheckCircle2, XCircle, Clock, AlertTriangle, RefreshCw, ChevronDown
} from "lucide-react";
import { toast } from "sonner";

const BACKEND = (process.env.REACT_APP_BACKEND_URL || '').replace(/^http:/, 'https:');

function api(path, opts = {}) {
  const token = localStorage.getItem("token");
  return fetch(`${BACKEND}/api${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(opts.headers || {}),
    },
  });
}

const STATUS_CONFIG = {
  present: { label: "Présent", color: "#16a34a", bg: "rgba(22,163,74,0.1)", icon: CheckCircle2 },
  absent:  { label: "Absent",  color: "#dc2626", bg: "rgba(220,38,38,0.1)", icon: XCircle },
  late:    { label: "Retard",  color: "#f59e0b", bg: "rgba(245,158,11,0.1)", icon: Clock },
  excused: { label: "Excusé",  color: "#6366f1", bg: "rgba(99,102,241,0.1)", icon: AlertTriangle },
};

export default function AttendancePage() {
  const { workspaceId } = useParams();
  const { canManage, user } = useAuth();
  const isCandidate = user?.role === "candidate" || user?.role === "student";

  const [records, setRecords] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterDate, setFilterDate] = useState(new Date().toISOString().split("T")[0]);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => { loadData(); }, [workspaceId, filterDate]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (isCandidate) {
        const res = await api(`/attendance/my?workspace_id=${workspaceId}`);
        if (res.ok) {
          const data = await res.json();
          setRecords(data.records || []);
        }
      } else {
        const res = await api(`/attendance/list?workspace_id=${workspaceId}&date=${filterDate}`);
        if (res.ok) {
          const data = await res.json();
          setRecords(data.records || []);
          setSchedules(data.schedules || []);
          setParticipants(data.participants || []);
        }
      }
    } catch (err) {
      console.error("Error loading attendance:", err);
    }
    setLoading(false);
  };

  const updateStatus = async (attendanceId, newStatus) => {
    try {
      const res = await api(`/attendance/${attendanceId}/status`, {
        method: "PUT",
        body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) {
        toast.success(`Statut mis à jour : ${STATUS_CONFIG[newStatus]?.label}`);
        setEditingId(null);
        loadData();
      } else toast.error("Erreur de mise à jour");
    } catch { toast.error("Erreur de connexion"); }
  };

  const exportPDF = () => {
    const token = localStorage.getItem("token");
    window.open(`${BACKEND}/api/attendance/export/pdf?workspace_id=${workspaceId}&date=${filterDate}&token=${token}`, "_blank");
  };

  const formatTime = (dt) => {
    try { return new Date(dt).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; }
  };
  const formatDateFR = (dt) => {
    try { return new Date(dt).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }); } catch { return ""; }
  };

  // Stats
  const stats = useMemo(() => {
    const s = { present: 0, absent: 0, late: 0, excused: 0, total: 0 };
    records.forEach((r) => { s[r.status] = (s[r.status] || 0) + 1; s.total++; });
    return s;
  }, [records]);

  // Group records by schedule
  const groupedBySchedule = useMemo(() => {
    const map = {};
    records.forEach((r) => {
      if (!map[r.schedule_id]) map[r.schedule_id] = [];
      map[r.schedule_id].push(r);
    });
    return map;
  }, [records]);

  if (loading) {
    return (
      <WorkspaceLayout>
        <div className="flex items-center justify-center py-32">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </WorkspaceLayout>
    );
  }

  return (
    <WorkspaceLayout>
      <div className="page-container animate-fade-in" style={{ maxWidth: 1100 }}>

        {/* ═══ Header ═══ */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16, marginBottom: 24 }}>
          <div>
            <h1 style={{ fontSize: 28, fontWeight: 700, display: "flex", alignItems: "center", gap: 12, color: "var(--foreground)" }}>
              <span style={{ background: "linear-gradient(135deg, #10B981, #34D399)", padding: 10, borderRadius: 12, display: "flex" }}>
                <UserCheck className="h-6 w-6" style={{ color: "white" }} />
              </span>
              {isCandidate ? "Mon historique de présence" : "Gestion des présences"}
            </h1>
            <p style={{ marginTop: 4, color: "var(--muted-foreground)", fontSize: 14 }}>
              {isCandidate ? "Retrouvez ici toutes vos signatures de présence." : "Consultez, modifiez et exportez les présences des candidats."}
            </p>
          </div>
          {canManage && (
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <input type="date" value={filterDate} onChange={(e) => setFilterDate(e.target.value)}
                style={{ borderRadius: 10, border: "1px solid var(--border)", padding: "8px 14px", fontSize: 13, background: "var(--background)" }} />
              <Button variant="outline" size="sm" onClick={loadData} style={{ gap: 6, borderRadius: 20, fontSize: 13 }}>
                <RefreshCw className="h-3.5 w-3.5" /> Actualiser
              </Button>
              <Button variant="outline" size="sm" onClick={exportPDF} style={{ gap: 6, borderRadius: 20, fontSize: 13 }}>
                <Download className="h-3.5 w-3.5" /> Export PDF
              </Button>
            </div>
          )}
        </div>

        {/* ═══ Stats (admin/trainer) ═══ */}
        {canManage && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 24 }}>
            {Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
              const Icon = cfg.icon;
              return (
                <div key={key} style={{
                  background: cfg.bg, border: `1px solid ${cfg.color}30`, borderRadius: 14, padding: "16px 20px",
                  display: "flex", alignItems: "center", gap: 14
                }}>
                  <div style={{ background: `${cfg.color}20`, padding: 10, borderRadius: 10, display: "flex" }}>
                    <Icon style={{ width: 20, height: 20, color: cfg.color }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 26, fontWeight: 800, color: cfg.color }}>{stats[key] || 0}</div>
                    <div style={{ fontSize: 12, color: "var(--muted-foreground)", fontWeight: 500 }}>{cfg.label}{stats[key] !== 1 ? "s" : ""}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ═══ Records Table ═══ */}
        {records.length === 0 ? (
          <Card style={{ borderRadius: 20, border: "2px dashed var(--border)" }}>
            <CardContent style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "64px 24px" }}>
              <UserCheck className="h-12 w-12" style={{ color: "var(--muted-foreground)", opacity: 0.4 }} />
              <h3 style={{ marginTop: 16, fontWeight: 600, fontSize: 16, color: "var(--muted-foreground)" }}>
                {isCandidate ? "Aucune présence enregistrée" : `Aucune présence pour le ${formatDateFR(filterDate)}`}
              </h3>
            </CardContent>
          </Card>
        ) : (
          <div style={{ background: "var(--card)", borderRadius: 16, border: "1px solid var(--border)", overflow: "hidden", boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "var(--muted)", borderBottom: "1px solid var(--border)" }}>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 700, fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: "var(--muted-foreground)" }}>Candidat</th>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 700, fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: "var(--muted-foreground)" }}>Cours</th>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 700, fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: "var(--muted-foreground)" }}>Heure cours</th>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 700, fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: "var(--muted-foreground)" }}>Signé à</th>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 700, fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: "var(--muted-foreground)" }}>Statut</th>
                  {canManage && (
                    <th style={{ padding: "12px 16px", textAlign: "center", fontWeight: 700, fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: "var(--muted-foreground)" }}>Actions</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {records.map((r, i) => {
                  const sc = STATUS_CONFIG[r.status] || STATUS_CONFIG.present;
                  const Icon = sc.icon;
                  return (
                    <tr key={r.attendance_id || i} style={{ borderBottom: "1px solid var(--border)", transition: "background 0.15s" }}
                      onMouseEnter={(e) => e.currentTarget.style.background = "var(--muted)"}
                      onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}>
                      <td style={{ padding: "12px 16px" }}>
                        <div style={{ fontWeight: 600 }}>{r.user_name}</div>
                        <div style={{ fontSize: 11, color: "var(--muted-foreground)" }}>{r.user_email}</div>
                      </td>
                      <td style={{ padding: "12px 16px", fontWeight: 500 }}>{r.course_title}</td>
                      <td style={{ padding: "12px 16px", color: "var(--muted-foreground)" }}>{formatTime(r.course_start)}</td>
                      <td style={{ padding: "12px 16px", color: "var(--muted-foreground)" }}>{formatTime(r.signed_at)}</td>
                      <td style={{ padding: "12px 16px" }}>
                        <Badge style={{ background: sc.bg, color: sc.color, border: `1px solid ${sc.color}30`, borderRadius: 20, fontSize: 11, fontWeight: 600, gap: 4, display: "inline-flex", alignItems: "center" }}>
                          <Icon style={{ width: 12, height: 12 }} /> {sc.label}
                        </Badge>
                        {r.modified_by && (
                          <div style={{ fontSize: 10, color: "var(--muted-foreground)", marginTop: 2 }}>
                            Modifié par {r.modified_by}
                          </div>
                        )}
                      </td>
                      {canManage && (
                        <td style={{ padding: "12px 16px", textAlign: "center" }}>
                          {editingId === r.attendance_id ? (
                            <div style={{ display: "flex", gap: 4, justifyContent: "center", flexWrap: "wrap" }}>
                              {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                                <button key={key} type="button"
                                  onClick={() => updateStatus(r.attendance_id, key)}
                                  style={{
                                    fontSize: 10, padding: "4px 10px", borderRadius: 12,
                                    background: key === r.status ? cfg.color : cfg.bg,
                                    color: key === r.status ? "white" : cfg.color,
                                    border: `1px solid ${cfg.color}50`,
                                    cursor: "pointer", fontWeight: 600
                                  }}>
                                  {cfg.label}
                                </button>
                              ))}
                            </div>
                          ) : (
                            <Button variant="ghost" size="sm" onClick={() => setEditingId(r.attendance_id)}
                              style={{ fontSize: 11, borderRadius: 20 }}>
                              Modifier
                            </Button>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </WorkspaceLayout>
  );
}
